import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldError } from 'react-hook-form';

import { StyledWrapper, StyledLabel, StyledErrorMessage } from '../styled';
import { LabelWrapper } from '../TextInput/styled';
import {
  StyledSelect,
  StyledOption,
  StyledOptionIcon,
  StyledOptionIconRemove,
} from './styled';

interface SelectInputControlledMultiV2Props {
  name?: string;
  label?: string;
  width?: string;
  value: { [key: string]: any }[];
  options: { [key: string]: any }[];
  error?: FieldError;
  isDisabled?: boolean;
  placeholder?: string;
  maxMenuHeight?: number;
  closeMenuOnSelect?: boolean;
  onChange: (
    value: { [key: string]: any }[],
    prevValue: { [key: string]: any }[],
  ) => void;
  onBlur?: () => void;
  labelAlignment?: string;
  labelStyle?: { [p: string]: string };
  count?: number;
  maxCount?: number;
  countFontSize?: number;
  countFontWeight?: number;
}

const OptionMulti = (props) => {
  const { innerProps, isSelected, data } = props;
  return (
    <div {...innerProps}>
      <StyledOption isSelected={isSelected}>
        {data.label}
        {!isSelected ? <StyledOptionIcon /> : <StyledOptionIconRemove />}
      </StyledOption>
    </div>
  );
};

const SelectInputControlledMultiV2: FC<SelectInputControlledMultiV2Props> = ({
  name,
  label,
  options,
  width,
  error,
  value,
  isDisabled,
  placeholder = '',
  closeMenuOnSelect = true,
  onChange,
  onBlur = () => {},
  labelAlignment,
  labelStyle = {},
  count,
  maxCount,
  countFontSize = 10,
  countFontWeight = 400,
}) => {
  const { t } = useTranslation();
  const showCount = maxCount != null;

  return (
    <StyledWrapper>
      {label && (
        <LabelWrapper>
          <StyledLabel
            error={error}
            htmlFor={name}
            labelAlignment={labelAlignment}
            style={labelStyle}
          >
            {label}{' '}
          </StyledLabel>
          {showCount && (
            <StyledLabel
              error={error}
              htmlFor={name}
              fontSize={countFontSize}
              fontWeight={countFontWeight}
            >
              {count ?? 0} / {maxCount}
            </StyledLabel>
          )}
        </LabelWrapper>
      )}
      <StyledSelect
        error={error}
        width={width}
        id={name}
        name={name}
        options={options}
        placeholder={placeholder}
        classNamePrefix="react-select"
        isDisabled={isDisabled}
        value={value}
        components={{ Option: OptionMulti }}
        isMulti={true}
        hideSelectedOptions={false}
        isClearable={false}
        minMenuHeight={250}
        maxMenuHeight={250}
        closeMenuOnSelect={closeMenuOnSelect}
        noOptionsMessage={() => t('common.noOptions')}
        onChange={(v) => onChange(v, value)}
        onBlur={onBlur}
        styles={{
          control: (base) => ({
            ...base,
            flexWrap: 'no-wrap',
          }),
          valueContainer: (base) => ({
            ...base,
            flex: 'auto',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            overflowX: 'scroll',
          }),
          multiValue: (base) => ({
            ...base,
            minWidth: '60px',
            backgroundColor: '#edf3f8',
          }),
          multiValueRemove: () => ({
            display: 'none',
          }),
          option: (base, state) => ({
            ...base,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            backgroundColor: state.isSelected
              ? '#edf3f8!important'
              : 'transparent!important',
            width: '77px',
            textAlign: 'center',
            padding: '3px 6px',
          }),
        }}
      />
      {error && (
        <StyledErrorMessage>
          {error.message || t(`errors.${error.type}`)}
        </StyledErrorMessage>
      )}
    </StyledWrapper>
  );
};

export default SelectInputControlledMultiV2;
